/*
 * Studio v2 — print geometry and print-file checks. No DOM: this file runs in the browser
 * (window.StudioPrint) and in Node tests (scripts/studio-print.test.mjs).
 *
 * Why it exists (2026-10-15, orders #MV1545 and #MV1540):
 * - A zone on the garment photo does not have the print area's shape (the blanket zone is square,
 *   its print area 20 x 15 in). The print file was scaled by the zone's width only, so a design
 *   fitted to the square zone ran off the bottom of the 20 x 15 canvas. area() is the print area
 *   drawn inside the zone at its true shape; designs are fitted to that, in the mockup and in the
 *   print file alike, so the file always holds the whole design.
 * - A 1266 px upload was shown 18 in wide on a blanket. place() caps a raster design at the width
 *   its pixels can print at MIN_PPI.
 * - Canvas PNGs carried a 72 DPI tag, and the POD floor sizes art by that tag. pngDpi() writes the
 *   real density (pHYs) into the file.
 */
(function (root) {
  const ART_DPI = 150; // print files: the whole print area at this density
  const MIN_PPI = 100; // the POD art spec's error line (warn < 150)
  const FILL = 0.92; // a design fills 92% of its print area
  // Partial opacity, as the POD art checker measures it (pod-image-service ALPHA_CLEAR /
  // ALPHA_OPAQUE / SEMI_TRANSPARENT_FRACTION_WARN): it warns above 10% of the inked pixels.
  const ALPHA_CLEAR = 6, ALPHA_OPAQUE = 250, PARTIAL_WARN = 0.1;
  // Near-solid pixels in a print file are made solid (a logo saved at 98% opacity, #MV1545).
  const ALPHA_HARDEN = 240;

  // The print area inside a zone: the largest rectangle of the print area's shape (size, inches),
  // centred in the zone. Without a size, the zone itself.
  function area(z) {
    const [wi, hi] = z.size || [];
    const out = { x: z.x, y: z.y, w: z.w, h: z.h };
    if (wi > 0 && hi > 0 && z.w > 0 && z.h > 0) {
      const ar = wi / hi;
      if (z.w / z.h > ar) { out.h = z.h; out.w = z.h * ar; } else { out.w = z.w; out.h = z.w / ar; }
      out.x = z.x + (z.w - out.w) / 2; out.y = z.y + (z.h - out.h) / 2;
    }
    out.cx = out.x + out.w / 2; out.cy = out.y + out.h / 2;
    return out;
  }

  // Where a design sits: fitted to FILL of the print area, centred. srcPx = the design's own
  // pixel width when it is a raster that cannot be redrawn larger (an upload); then the printed
  // width is capped at srcPx / MIN_PPI inches. Returns the box in zone (photo) pixels plus
  // inches: [w, h] printed, ppi (null for vector-like designs) and capped.
  // The logo-as-is path (2026-10-19 revamp Phase 2, proposal section 5) adds three options:
  //   widthIn  the printed width the shopper chose (small / medium / large), in inches; never more than fits
  //   pos      { x, y } in -1..1: where the design sits in the free room of the print area (0, 0 = centred,
  //            -1 = against the left / top edge). The box always stays inside the print area. Without a pos.y the
  //            design sits at the zone's anchor: the top edge for a full chest (z.anchor 'top'), else the middle.
  //   minPpi   the sharpness floor of the cap (the logo path uses ART_DPI, 150: what the POD pre-flight wants)
  // sharpIn is the widest the design prints at minPpi (null when it is not capped).
  function place(z, artW, artH, opts = {}) {
    const a = area(z), ar = artW / artH || 1, fill = opts.fill || FILL;
    let w = Math.min(a.w * fill, a.h * fill * ar), h = w / ar;
    const perIn = z.size && z.size[0] > 0 ? a.w / z.size[0] : 0; // photo px per printed inch
    if (opts.widthIn > 0 && perIn && opts.widthIn * perIn < w) { w = opts.widthIn * perIn; h = w / ar; }
    let capped = false, ppi = null, sharpIn = null;
    if (opts.srcPx && perIn) {
      const minPpi = opts.minPpi || MIN_PPI, maxW = (opts.srcPx / minPpi) * perIn;
      sharpIn = opts.srcPx / minPpi;
      if (w > maxW) { h *= maxW / w; w = maxW; capped = true; }
      ppi = opts.srcPx / (w / perIn);
    }
    const inches = perIn ? [w / perIn, h / perIn] : null;
    const py0 = z.anchor === 'top' ? -1 : 0, hasY = opts.pos && opts.pos.y != null;
    const px = opts.pos ? Math.max(-1, Math.min(1, +opts.pos.x || 0)) : 0, py = hasY ? Math.max(-1, Math.min(1, +opts.pos.y || 0)) : py0;
    const cx = a.cx + (px * Math.max(0, a.w - w)) / 2, cy = a.cy + (py * Math.max(0, a.h - h)) / 2;
    return { w, h, cx, cy, area: a, inches, ppi, capped, sharpIn };
  }
  // The pos that puts a box of w x h with its centre nearest (cx, cy) inside the print area a (for dragging).
  function posFor(a, w, h, cx, cy) {
    const fx = a.w - w, fy = a.h - h;
    // No room to move up or down: no y, so a later smaller size still sits at the zone's anchor.
    return { x: fx > 0 ? Math.max(-1, Math.min(1, ((cx - a.cx) * 2) / fx)) : 0, y: fy > 0 ? Math.max(-1, Math.min(1, ((cy - a.cy) * 2) / fy)) : undefined };
  }

  // Designs that are fixed pixels and cannot be redrawn larger, so their printed width is capped: the
  // shopper's upload and AI-drawn art (about 1024 px, under 100 ppi across a 20 in blanket; Cal 2026-10-15:
  // "cap AI art too"). Lettering is drawn again at print size, so it is not capped.
  const capped = (kind) => kind === 'upload' || kind === 'art';

  // Print-file density for a print area: ART_DPI, raised for small areas so the file meets the POD art floor
  // (pod-image-service: at least 1000 px on the long side and 500 px on the short side). A 4 x 4 in left chest
  // at 150 dpi was 600 px and the pre-flight refused it as too-small (2026-10-15); it is now 250 dpi, 1000 px.
  const FLOOR_LONG = 1000, FLOOR_SHORT = 500;
  // The POD pre-flight measures a file's resolution against the location's box in the POD catalogue
  // (pod_decoration_location, read 2026-10-19: FC and FB 14.5 x 18 in, FLC = our LC 5 x 5, BKT 20 x 15), not
  // against our print area: effective ppi = the larger of px / box width and px / box height. A 150 dpi file of our
  // 12 in full chest is 1800 px, which it reads as 124 ppi ("may look soft"). So the file density is raised until
  // the file reads ART_DPI against that box. The density tag still says the true size, so it prints the same size.
  // Only the logo-as-is file does this (opts.pod): the checker scales any file over 2048 px down before it counts
  // see-through pixels (pod-image-service ANALYSIS_LONG_EDGE_PX), so a bigger file of fine lines reads as partly
  // see-through even when every pixel is solid; AI art keeps today's 150 dpi file.
  const POD_BOX = { FC: [14.5, 18], FCZC: [14.5, 18], FB: [14.5, 18], LC: [5, 5], RC: [5, 5], BKT: [20, 15], BCT: [20, 15] };
  function fileDpi(size, key, opts = {}) {
    const [w, h] = size || [];
    if (!(w > 0 && h > 0)) return ART_DPI;
    const box = opts.pod ? POD_BOX[key] : null;
    const pod = box ? ART_DPI * Math.min(box[0] / w, box[1] / h) : 0;
    return Math.ceil(Math.max(ART_DPI, pod, FLOOR_LONG / Math.max(w, h), FLOOR_SHORT / Math.min(w, h)));
  }
  // Print-file canvas for a zone: the whole print area at fileDpi → [width, height, dpi].
  function canvasSize(z, opts) {
    const dpi = fileDpi(z.size, z.key, opts);
    return [Math.round(z.size[0] * dpi) || 1800, Math.round(z.size[1] * dpi) || 1950, dpi];
  }

  // Partial-opacity share of RGBA pixels (Uint8ClampedArray), measured like the POD checker.
  function partialAlpha(d) {
    let inked = 0, partial = 0;
    for (let i = 3; i < d.length; i += 4) { const a = d[i]; if (a > ALPHA_CLEAR) { inked++; if (a < ALPHA_OPAQUE) partial++; } }
    return inked ? partial / inked : 0;
  }
  // In place: near-solid → solid, near-clear → clear. Returns the partial share left afterwards.
  function harden(d) {
    for (let i = 3; i < d.length; i += 4) { if (d[i] >= ALPHA_HARDEN) d[i] = 255; else if (d[i] <= ALPHA_CLEAR) d[i] = 0; }
    return partialAlpha(d);
  }

  // In place: every pixel solid or clear, cut at half opacity (the print lane's fix for a see-through edge, #MV1554).
  // The logo-as-is print file gets this: the POD press needs 100% solid ink, and the pre-flight warns on partial opacity.
  function solid(d) {
    for (let i = 3; i < d.length; i += 4) d[i] = d[i] >= 128 ? 255 : 0;
    return partialAlpha(d);
  }

  // PNG bytes → the same PNG with a pHYs chunk saying `dpi` (any old pHYs removed). Pixels untouched.
  const CRC = (() => { const t = new Uint32Array(256); for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; } return t; })();
  const crc32 = (u8) => { let c = 0xffffffff; for (let i = 0; i < u8.length; i++) c = CRC[(c ^ u8[i]) & 0xff] ^ (c >>> 8); return (c ^ 0xffffffff) >>> 0; };
  function pngDpi(bytes, dpi) {
    const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    const SIG = [137, 80, 78, 71, 13, 10, 26, 10];
    if (u8.length < 33 || SIG.some((b, i) => u8[i] !== b)) throw new Error('not a PNG');
    const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
    const parts = [u8.subarray(0, 8)];
    let off = 8, wrote = false;
    while (off + 12 <= u8.length) {
      const len = dv.getUint32(off), type = String.fromCharCode(u8[off + 4], u8[off + 5], u8[off + 6], u8[off + 7]);
      const end = off + 12 + len;
      if (type !== 'pHYs') parts.push(u8.subarray(off, end));
      if (type === 'IHDR' && !wrote) {
        const ppm = Math.round(dpi / 0.0254), c = new Uint8Array(21), cv = new DataView(c.buffer);
        cv.setUint32(0, 9); c.set([112, 72, 89, 115], 4); cv.setUint32(8, ppm); cv.setUint32(12, ppm); c[16] = 1;
        cv.setUint32(17, crc32(c.subarray(4, 17)));
        parts.push(c); wrote = true;
      }
      off = end;
      if (type === 'IEND') break;
    }
    const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
    let o = 0; for (const p of parts) { out.set(p, o); o += p.length; }
    return out;
  }
  // The density a PNG says it has, in dpi (null when untagged).
  function pngDensity(bytes) {
    const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes), dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
    for (let off = 8; off + 12 <= u8.length;) {
      const len = dv.getUint32(off), type = String.fromCharCode(u8[off + 4], u8[off + 5], u8[off + 6], u8[off + 7]);
      if (type === 'pHYs') return u8[off + 16] === 1 ? dv.getUint32(off + 8) * 0.0254 : null;
      if (type === 'IDAT' || type === 'IEND') return null;
      off += 12 + len;
    }
    return null;
  }

  // Plain-language notes about a printInfo() result, for the shopper (review screen) and for staff (the order
  // line's Art check). [] when there is nothing to say.
  function notes(info) {
    if (!info) return [];
    const out = [];
    if (info.capped && info.inches && info.asIs) out.push(`Your file prints sharp up to ${info.inches[0].toFixed(1)} in wide, so we print it at that size. Upload a bigger file to print it larger.`);
    else if (info.capped && info.inches) out.push(info.kind === 'art'
      ? `This artwork prints ${info.inches[0].toFixed(1)} in wide, the largest it stays sharp.`
      : `Your image is small, so it prints ${info.inches[0].toFixed(1)} in wide, the largest it stays sharp. Upload a bigger file to print it larger.`);
    if (info.partial != null && info.partial > PARTIAL_WARN) out.push(`About ${Math.round(info.partial * 100)}% of your image is see-through (faded or semi-transparent), and those parts can print blotchy. We check every file before printing.`);
    return out;
  }

  const api = { POD_BOX, ART_DPI, MIN_PPI, FILL, PARTIAL_WARN, area, place, posFor, capped, fileDpi, canvasSize, partialAlpha, harden, solid, pngDpi, pngDensity, notes };
  root.StudioPrint = api;
  if (typeof module === 'object' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
